import { getMatchDisplayNumber, getMatchPlayerScore, getPlayerId, isMatchList } from '../../utils/bracket';
import { getAllBracketSetDivisions } from '../../utils/bracketSet';
import { useI18n } from '../../i18n';
import { roundLabelFromSize, translateDivisionLabel, translateRoundLabel } from '../../i18n/labels';

const MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' };

function formatRank(t, rank) {
  if (rank == null) return '-';
  return `${MEDALS[rank] ?? ''} ${t('bracket.results.rank', { rank })}`.trim();
}

function formatScore(t, score) {
  if (!score) return '-';
  return t('bracket.results.scoreDetail', {
    total: score.total ?? '-',
    head: score.head ?? '-',
    body: score.body ?? '-',
  });
}

function formatRecord(t, r) {
  return r.draws
    ? t('bracket.results.recordWithDraws', { wins: r.wins, draws: r.draws, losses: r.losses })
    : t('bracket.results.record', { wins: r.wins, losses: r.losses });
}

function groupResults(results) {
  const groups = [];
  results.forEach((result) => {
    let group = groups.find((g) => g.key === result.divisionKey);
    if (!group) {
      group = { key: result.divisionKey, label: result.divisionLabel, rows: [] };
      groups.push(group);
    }
    group.rows.push(result);
  });
  return groups;
}

function MatchLog({ bracketSet }) {
  const { t } = useI18n();
  const divisions = getAllBracketSetDivisions(bracketSet);

  return divisions.map(({ key, label, bracket }) => {
    const rows = bracket.rounds.flatMap((round, roundIndex) =>
      round.matches
        .map((match, matchIndex) => ({
          match,
          matchIndex,
          roundIndex,
          title: !isMatchList(bracket) && bracket.bracketSize
            ? roundLabelFromSize(bracket.bracketSize, roundIndex)
            : translateRoundLabel(round.title),
        }))
        .filter(({ match }) => match.player1 && match.player2)
    );
    if (rows.length === 0) return null;

    return (
      <div key={key} className="results-match-log">
        <h4 className="results-subtitle">
          {t('bracket.results.matchLog')}
          {label ? ` · ${translateDivisionLabel(label)}` : ''}
        </h4>
        <div className="member-records-wrap">
          <table className="member-records-table">
            <thead>
              <tr>
                <th>{t('bracket.results.col.match')}</th>
                <th>{t('bracket.results.col.round')}</th>
                <th>{t('bracket.results.col.player1')}</th>
                <th>{t('bracket.results.col.score')}</th>
                <th>{t('bracket.results.col.player2')}</th>
                <th>{t('bracket.results.col.score')}</th>
                <th>{t('bracket.results.col.winner')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ match, matchIndex, roundIndex, title }) => {
                const winnerId = getPlayerId(match.winner);
                return (
                  <tr key={`${roundIndex}-${matchIndex}`}>
                    <td>{t('bracket.matchNo', { no: getMatchDisplayNumber(bracket, roundIndex, matchIndex) })}</td>
                    <td>{title}</td>
                    <td className={winnerId === getPlayerId(match.player1) ? 'cell-winner' : ''}>
                      {match.player1.participantName}
                    </td>
                    <td>{formatScore(t, getMatchPlayerScore(match, match.player1))}</td>
                    <td className={winnerId === getPlayerId(match.player2) ? 'cell-winner' : ''}>
                      {match.player2.participantName}
                    </td>
                    <td>{formatScore(t, getMatchPlayerScore(match, match.player2))}</td>
                    <td>
                      {match.draw
                        ? t('bracket.results.draw')
                        : match.winner
                          ? match.winner.participantName
                          : t('bracket.results.undecided')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  });
}

/**
 * 확정된 결과: 부문별 순위표 (+ 경기 기록).
 * matchList(1:1 경기)는 순위 없이 선수별 전적·점수만 이름순으로 보여준다.
 */
function BracketResultsPanel({ results, bracketSet, showMatchLog = true, matchList = false }) {
  const { t, lang } = useI18n();
  if (!results?.length) return null;
  const groups = groupResults(results);
  if (matchList) {
    groups.forEach((group) =>
      group.rows.sort((a, b) => (a.participantName ?? '').localeCompare(b.participantName ?? '', lang))
    );
  }

  return (
    <section className="results-panel">
      <h3 className="results-title">
        {t(matchList ? 'bracket.results.titleMatches' : 'bracket.results.title')}
      </h3>

      {groups.map((group) => {
        const podium = matchList
          ? []
          : group.rows.filter((r) => r.finalRank != null && r.finalRank <= 3);
        return (
          <div key={group.key} className="results-division">
            {group.label && (
              <h4 className="results-subtitle">{translateDivisionLabel(group.label)}</h4>
            )}
            {podium.length > 0 && (
              <div className="results-podium">
                {podium.map((r) => (
                  <div key={r.applicationId} className={`podium-card podium-rank-${r.finalRank}`}>
                    <span className="podium-medal">{MEDALS[r.finalRank]}</span>
                    <span className="podium-name">{r.participantName}</span>
                    <span className="podium-gym">{r.gymName ?? t('common.noGym')}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="member-records-wrap">
              <table className="member-records-table">
                <thead>
                  <tr>
                    {!matchList && <th>{t('bracket.results.col.rank')}</th>}
                    <th>{t('bracket.results.col.name')}</th>
                    <th>{t('bracket.results.col.gym')}</th>
                    <th>{t('bracket.results.col.record')}</th>
                    <th>{t('bracket.results.col.headTotal')}</th>
                    <th>{t('bracket.results.col.bodyTotal')}</th>
                    <th>{t('bracket.results.col.finalTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((r) => (
                    <tr key={r.applicationId}>
                      {!matchList && <td>{formatRank(t, r.finalRank)}</td>}
                      <td className="cell-name">{r.participantName}</td>
                      <td>{r.gymName ?? t('common.noGym')}</td>
                      <td>{formatRecord(t, r)}</td>
                      <td>{r.headScore}</td>
                      <td>{r.bodyScore}</td>
                      <td>
                        <strong>{r.totalScore}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {showMatchLog && bracketSet && <MatchLog bracketSet={bracketSet} />}
    </section>
  );
}

export default BracketResultsPanel;
